import React, { useState } from 'react';
import './Pricing.css';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import FreeSiteBanner from '../components/FreeSiteBanner';

// ── UNIFIED FEATURES ─────────────────────────────────────────────
const FEATURES = [
  { id: 'seo',        label: 'Advanced SEO',              icon: '🔍', price: 300   },
  { id: 'booking',    label: 'Booking System',            icon: '📅', price: 400   },
  { id: 'ecom',       label: 'E-Commerce Store',          icon: '🛒', price: 800   },
  { id: 'blog',       label: 'Blog / CMS',                icon: '✍️', price: 300   },
  { id: 'chat',       label: 'Live Chat',                 icon: '💬', price: 150   },
  { id: 'analytics',  label: 'Analytics Dashboard',       icon: '📊', price: 150   },
  { id: 'auth',       label: 'User Accounts & Auth',      icon: '🔐', price: 600   },
  { id: 'messaging',  label: 'In-App Messaging',          icon: '✉️', price: 1200  },
  { id: 'payments',   label: 'Payments & Subscriptions',  icon: '💳', price: 800   },
  { id: 'push',       label: 'Push Notifications',        icon: '🔔', price: 500   },
  { id: 'admin',      label: 'Admin Dashboard',           icon: '🖥️', price: 900   },
  { id: 'api',        label: 'API / Third-Party Integration', icon: '🔗', price: 600 },
  { id: 'maps',       label: 'Maps & Location Services',  icon: '🗺️', price: 400   },
  { id: 'email',      label: 'Email Automation',          icon: '📧', price: 350   },
  { id: 'ai',         label: 'AI Features',               icon: '🤖', price: 1500  },
  { id: 'video',      label: 'Video Streaming',           icon: '📹', price: 1800  },
  { id: 'database',   label: 'Custom Database',           icon: '🗄️', price: 700   },
  { id: 'multiplatform', label: 'Multi-Platform (iOS + Android)', icon: '📱', price: 2000 },
];

// ── PRICING LOGIC ─────────────────────────────────────────────────
// Returns { low, high } — range shown to user, both saved to Firestore
function calcRange(type, pages, users, features) {
  let low = 0, high = 0;

  if (type === 'web') {
    low  = Math.max(pages * 80,  199);
    high = Math.max(pages * 160, 599);
    if (users >= 500 && users < 2000) { low += 200;  high += 500;  }
    else if (users >= 2000)           { low += 500;  high += 1200; }
  } else if (type === 'app') {
    low  = Math.max(2500 + (pages - 1) * 250, 2500);
    high = Math.max(5000 + (pages - 1) * 500, 5000);
    if (users >= 500 && users < 2000) { low += 800;  high += 1800; }
    else if (users >= 2000)           { low += 2000; high += 4500; }
  } else {
    const wLow  = Math.max(pages * 80,  199);
    const wHigh = Math.max(pages * 160, 599);
    const aLow  = Math.max(2500 + (pages - 1) * 250, 2500);
    const aHigh = Math.max(5000 + (pages - 1) * 500, 5000);
    low  = Math.round((wLow  + aLow)  * 0.9);
    high = Math.round((wHigh + aHigh) * 0.9);
    if (users >= 500 && users < 2000) { low += 1000; high += 2300; }
    else if (users >= 2000)           { low += 2500; high += 5700; }
  }

  FEATURES.forEach(f => {
    if (features.includes(f.id)) {
      low  += f.price;
      high += Math.round(f.price * 1.6);
    }
  });

  return { low, high };
}

// ── COMPONENT ─────────────────────────────────────────────────────
export default function Pricing() {
  const navigate = useNavigate();

  const [projectType,     setProjectType]     = useState('web');
  const [pages,           setPages]           = useState(5);
  const [users,           setUsers]           = useState(100);
  const [selectedFeatures,setSelectedFeatures]= useState([]);
  const [description,     setDescription]     = useState('');
  const [name,            setName]            = useState('');
  const [email,           setEmail]           = useState('');
  const [submitted,       setSubmitted]       = useState(false);
  const [submitting,      setSubmitting]      = useState(false);
  const [estimateRange,   setEstimateRange]   = useState(null);

  const toggleFeature = (id) =>
    setSelectedFeatures(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

  const switchType = (type) => {
    setProjectType(type);
    setSelectedFeatures([]);
    setSubmitted(false);
    setEstimateRange(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const { low, high } = calcRange(projectType, pages, users, selectedFeatures);
const data = {
  name, email, projectType, pages,
  dailyUsers: users,
  features: selectedFeatures,
  description,
  estimatedLow:  low,
  estimatedHigh: high,
  status:        'pending',
  completed:     false,
  createdAt:     Timestamp.now(),
  source:        'estimate',
};
    try {
      await addDoc(collection(db, 'estimates'), data);
      setEstimateRange({ low, high });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pricing-container">

      <FreeSiteBanner />

      {/* ── HEADER ── */}
      <div className="pricing-header">
        <span className="pricing-eyebrow">Transparent Pricing</span>
        <h1>Simple Pricing. <em>No Surprises.</em></h1>
        <p>
          Your website or app is the best investment you'll make in your business.
          Use the estimator below to get an instant quote — no commitment required.
        </p>
      </div>

      {/* ── PRICING CARDS ── */}
      <div className="pricing-grid">
        <div className="pricing-card">
          <h3>Static Site</h3>
          <div className="pricing-price">$100<span> / page</span></div>
          <p className="pricing-desc">Get online fast with a clean, professional presence that actually converts.</p>
          <ul className="pricing-features">
            <li>Up to 5 pages</li>
            <li>Mobile responsive</li>
            <li>Contact form</li>
            <li>Basic SEO setup</li>
            <li>2 revision rounds</li>
          </ul>
          <button className="btn-outline-dark" onClick={() => navigate('/contact')}>Get Started</button>
        </div>

        <div className="pricing-card featured">
          <div className="pricing-badge">Most Popular</div>
          <h3>Business Site</h3>
          <div className="pricing-price">$1,499<span> one-time</span></div>
          <p className="pricing-desc">A full conversion-focused site built to grow with your business.</p>
          <ul className="pricing-features">
            <li>Up to 10 pages</li>
            <li>Custom design</li>
            <li>Booking or e-commerce</li>
            <li>Full SEO optimization</li>
            <li>Unlimited revisions</li>
          </ul>
          <button className="btn-outline-white" onClick={() => navigate('/contact')}>Get Started</button>
        </div>

        <div className="pricing-card">
          <h3>Custom / App</h3>
          <div className="pricing-price">Custom<span> quote</span></div>
          <p className="pricing-desc">Portals, web apps, and complex builds tailored to your exact needs.</p>
          <ul className="pricing-features">
            <li>Full scoping session</li>
            <li>Custom functionality</li>
            <li>API integrations</li>
            <li>Ongoing support options</li>
            <li>Dedicated project manager</li>
          </ul>
          <button className="btn-outline-dark" onClick={() => navigate('/contact')}>Let's Talk</button>
        </div>
      </div>

      {/* ── ESTIMATOR ── */}
      <div className="estimator">
        <div className="estimator-header">
          <div className="estimator-eyebrow">Price Estimator</div>
          <h2>What Will My Project Cost?</h2>
          <p>Tell us about your project and get an instant estimate — no commitment required.</p>
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit}>

            {/* TYPE TOGGLE */}
            <div className="type-toggle">
              {[
                { id: 'web',  icon: '🌐', label: 'Website',         sub: 'Landing pages, business sites, e-commerce' },
                { id: 'app',  icon: '📱', label: 'Mobile / Web App', sub: 'iOS, Android, full-stack web apps' },
                { id: 'both', icon: '🚀', label: 'Website + App',    sub: 'Complete digital presence' },
              ].map(t => (
                <button type="button" key={t.id}
                  className={`type-btn${projectType === t.id ? ' selected' : ''}`}
                  onClick={() => switchType(t.id)}>
                  <span className="type-btn-icon">{t.icon}</span>
                  <span className="type-btn-label">{t.label}</span>
                  <span className="type-btn-sub">{t.sub}</span>
                </button>
              ))}
            </div>

            {/* DESCRIPTION */}
            <div className="description-group">
              <label className="description-label">Tell us about your project</label>
              <textarea
                className="description-input"
                placeholder="What are you building? Who is it for? What problem does it solve? The more detail, the better your estimate."
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                required
              />
            </div>

            {/* SLIDERS */}
            <div className="estimator-sliders">
              <div className="slider-group">
                <div className="slider-label">
                  <span>Number of Pages / Screens</span>
                  <span className="slider-value">{pages}</span>
                </div>
                <input type="range" min={1} max={30} value={pages}
                  onChange={e => setPages(Number(e.target.value))} />
                <div className="slider-ticks">
                  <span>1</span><span>8</span><span>15</span><span>22</span><span>30+</span>
                </div>
              </div>

              <div className="slider-group">
                <div className="slider-label">
                  <span>Expected Daily Users</span>
                  <span className="slider-value">{users >= 5000 ? '5,000+' : users.toLocaleString()}</span>
                </div>
                <input type="range" min={10} max={5000} step={10} value={users}
                  onChange={e => setUsers(Number(e.target.value))} />
                <div className="slider-ticks">
                  <span>10</span><span>500</span><span>1,000</span><span>2,500</span><span>5,000+</span>
                </div>
              </div>
            </div>

            {/* FEATURES */}
            <div className="estimator-features">
              <div className="features-label">
                Features <span className="features-hint">— select everything you need</span>
              </div>
              <div className="features-grid">
                {FEATURES.map(f => (
                  <button type="button" key={f.id}
                    className={`feature-chip${selectedFeatures.includes(f.id) ? ' selected' : ''}`}
                    onClick={() => toggleFeature(f.id)}>
                    <span className="feature-chip-icon">{f.icon}</span>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* LEAD CAPTURE */}
            <div className="estimator-lead">
              <div className="lead-label">Where should we send your estimate?</div>
              <div className="lead-fields">
                <input type="text" placeholder="Your name" value={name}
                  onChange={e => setName(e.target.value)} required className="lead-input" />
                <input type="email" placeholder="Your email" value={email}
                  onChange={e => setEmail(e.target.value)} required className="lead-input" />
                <button type="submit" className="btn-bright" disabled={submitting}>
                  {submitting ? 'Calculating...' : 'Get My Estimate →'}
                </button>
              </div>
              <p className="lead-privacy">We'll never share your information with anyone.</p>
            </div>

          </form>
        ) : (
          /* ── RESULT SCREEN ── */
          <div className="estimate-result-screen">
            <div className="result-top">
              <div className="result-check">✓</div>
              <div>
                <div className="result-sent">Estimate sent to {email}</div>
                <div className="result-sub">Here's your project estimate:</div>
              </div>
            </div>

            <div className="result-price-block">
              <div className="result-price-label">Estimated Project Range</div>
              <div className="result-price">${estimateRange?.low?.toLocaleString()} <span className="result-dash">–</span> ${estimateRange?.high?.toLocaleString()}<span> CAD</span></div>
              <div className="result-price-note">
                Final pricing depends on your exact requirements. Complex projects may vary, this is your starting estimate.
              </div>
            </div>

            <div className="result-cta">
              <h3>Ready to Start Building?</h3>
              <p>Contact Matt today and let's turn your idea into something real.</p>
              <div className="result-cta-btns">
                <button className="btn-bright" onClick={() => navigate('/contact')}>
                  Contact Matt Today →
                </button>
                <a className="btn-cta-secondary"
                  href="https://calendly.com/mattduarte56/15min"
                  target="_blank" rel="noopener noreferrer">
                  Book a Free Call
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── BOTTOM CTA ── */}
      <div className="pricing-cta">
        <h2>Not Sure What You Need?</h2>
        <p>Book a free 15-minute call, Matt will tell you exactly what makes sense for your budget and goals.</p>
        <button className="btn-bright"
          onClick={() => window.open('https://calendly.com/mattduarte56/15min', '_blank')}>
          Book My Free Call →
        </button>
      </div>

    </div>
  );
}